import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { XCircle, ChevronRight, RefreshCw } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';

// Dashboard sub-components ("Panel" redesign — course-centric)
import GreetingBar from '../components/dashboard/GreetingBar';
import ContinueLearningCard from '../components/dashboard/ContinueLearningCard';
import CourseLessonList from '../components/dashboard/CourseLessonList';
import DashboardSidebar from '../components/dashboard/DashboardSidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kabin Memurluğu ana kurs id'si — Navbar/useCabinEnrolled ile aynı sabit.
// Yeni bir /enrollments/{id} çağrısı yapmıyoruz; zaten çekilen enrollments
// listesinden türetiyoruz (bkz. cabinEnrolled hesaplaması aşağıda).
const CABIN_COURSE_ID = 'course_cabin_main_v1';

/* ─── Sub-components defined inline ────────────────────────────────────── */

function DocRejectedAlert({ count, onClick }) {
  const { t } = useLanguage();
  return (
    <div
      onClick={onClick}
      className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4 cursor-pointer hover:bg-red-100 transition-colors"
      role="alert"
      data-testid="doc-rejected-alert"
    >
      <div className="flex items-center gap-3">
        <XCircle className="text-red-500 shrink-0" size={22} />
        <div className="flex-1">
          <div className="font-bold text-red-700 text-sm">
            {t('dashboard.docRejected').replace('{{count}}', count)}
          </div>
          <div className="text-xs text-red-600">
            {t('dashboard.docRejected.fix')}
          </div>
        </div>
        <ChevronRight className="text-red-500 shrink-0" size={18} />
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-5">
      {/* GreetingBar */}
      <Skeleton className="h-16 w-full rounded-2xl" />
      {/* ContinueLearningCard */}
      <Skeleton className="h-28 w-full rounded-2xl" />
      {/* 2-col layout (1fr / 320px, matches Panel redesign) */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-5">
        {/* Lesson list */}
        <div>
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
        {/* Sidebar */}
        <div className="flex flex-col gap-4">
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-24 w-full rounded-2xl" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </div>
    </div>
  );
}

function DashboardError({ t, onRetry }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F4F7F5] px-4">
      <div className="card-duo p-10 text-center max-w-sm w-full">
        <XCircle size={48} strokeWidth={1.5} className="mx-auto text-rose-400 mb-4" />
        <h2 className="font-duo-display text-xl text-[var(--text-primary)] mb-2">
          {t('dashboard.error.title')}
        </h2>
        <p className="text-sm text-gray-500 mb-6">{t('dashboard.error.sub')}</p>
        <button
          onClick={onRetry}
          className="btn-duo-primary inline-flex items-center gap-2"
        >
          <RefreshCw size={18} />
          {t('common.retry')}
        </button>
      </div>
    </div>
  );
}

/* ─── Main Dashboard ────────────────────────────────────────────────────── */

export default function Dashboard() {
  const { user, authHeaders } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [enrollments, setEnrollments] = useState([]);
  const [profile, setProfile] = useState(null);
  const [progressSummary, setProgressSummary] = useState(null);
  const [docStats, setDocStats] = useState({ pending: 0, rejected: 0 });
  const [primaryLessons, setPrimaryLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [enrollRes, profileRes] = await Promise.all([
        axios.get(`${API}/enrollments`, { headers: authHeaders() }),
        axios.get(`${API}/profile`, { headers: authHeaders() }),
      ]);
      setEnrollments(enrollRes.data.enrollments || []);
      setProfile(profileRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  // Progress summary (non-critical — failure silently ignored)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/students/me/progress`, {
          headers: authHeaders(),
        });
        setProgressSummary(res.data);
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Document stats (non-critical)
  useEffect(() => {
    axios
      .get(`${API}/documents/me`, { headers: authHeaders() })
      .then(res => {
        const s = res.data?.stats || {};
        setDocStats({ pending: s.pending || 0, rejected: s.rejected || 0 });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch lessons for primary enrollment (non-critical fallback)
  // Only needed if enrollment doesn't already carry lessons array
  useEffect(() => {
    if (enrollments.length === 0) return;
    const primary = enrollments[0];
    if (!primary?.course) return;

    // Prefer embedded lessons
    const embedded =
      primary.lessons ||
      primary.course?.lessons ||
      [];
    if (embedded.length > 0) {
      setPrimaryLessons(embedded);
      return;
    }

    // Fallback: fetch from course detail
    const courseId =
      primary.course.course_id ||
      primary.course._id ||
      primary.course.id;
    if (!courseId) return;

    axios
      .get(`${API}/courses/${courseId}`, { headers: authHeaders() })
      .then(res => {
        const lessons =
          res.data?.lessons ||
          res.data?.course?.lessons ||
          [];
        setPrimaryLessons(lessons);
      })
      .catch(() => {
        // leave empty — CourseLessonList handles empty gracefully
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollments]);

  /* ── Derived values ──────────────────────────────────────────────── */
  const streak  = profile?.streak   ?? user?.streak   ?? 0;
  const totalXp = profile?.total_xp ?? user?.xp       ?? 0;
  const primaryEnrollment = enrollments[0] ?? null;
  const primaryCourseId =
    primaryEnrollment?.course?.course_id ||
    primaryEnrollment?.course?._id ||
    primaryEnrollment?.course_id ||
    null;

  // Rozet (badge) verisi henüz bu sayfaya bağlı bir endpoint'ten gelmiyor
  // (bkz. /gamification/badges — kapsam dışı, yeni endpoint eklenmedi).
  // Zarif fallback: 0 / kilitli rozet slotları gösterilir.
  const achievements = [];
  const badgeCount = achievements.length;

  // Kabin Memurluğu mülakat simülasyonu promosu — yalnızca öğrenci gerçekten
  // bu kursa kayıtlıysa gösterilir. Ekstra istek YOK: zaten çekilmiş olan
  // /api/enrollments listesinden türetiliyor (Navbar'daki useCabinEnrolled
  // hook'u ayrı bir /enrollments/{course_id} isteği kullanıyor; burada aynı
  // veriyi tekrar sorgulamadan, elimizdeki enrollments state'inden kontrol ediyoruz).
  const cabinEnrolled = enrollments.some(
    e => e.course_id === CABIN_COURSE_ID || e.course?.course_id === CABIN_COURSE_ID
  );

  /* ── Error state ─────────────────────────────────────────────────── */
  if (error) {
    return <DashboardError t={t} onRetry={fetchData} />;
  }

  /* ── Main render ─────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#F4F7F5]" data-testid="dashboard-page">
      {/* Top accent strip — green → gold gradient (Panel redesign) */}
      <div
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 h-1 z-30"
        style={{ background: 'linear-gradient(90deg,#34CC67,#C9A227)' }}
      />

      {/* Content wrapper */}
      <div className="max-w-[1200px] w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12">

        {/* Document rejection alert */}
        {docStats.rejected > 0 && (
          <DocRejectedAlert
            count={docStats.rejected}
            onClick={() => navigate('/documents')}
          />
        )}

        {/* Loading skeleton */}
        {loading ? (
          <DashboardSkeleton />
        ) : (
          <>
            <GreetingBar
              user={profile?.user || user}
              streak={streak}
              totalXp={totalXp}
              badgeCount={badgeCount}
            />

            <ContinueLearningCard enrollment={primaryEnrollment} />

            <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-5 items-start">
              {/* Lesson list — main column */}
              <div className="min-w-0">
                <CourseLessonList
                  enrollment={primaryEnrollment}
                  lessons={primaryLessons}
                />
              </div>

              {/* Sidebar — 320px column */}
              <DashboardSidebar
                weeklyData={progressSummary?.weekly_activity || []}
                achievements={achievements}
                user={profile?.user || user}
                showInterviewPromo={cabinEnrolled}
                primaryCourseId={primaryCourseId}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
